import { motion } from 'framer-motion'
import { Globe, Handshake, TrendingUp, Target, Users, Award } from 'lucide-react'
import { fadeIn, staggerContainer } from '../utils/motion'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

const Partnerships = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  const services = [
    {
      icon: Globe,
      titleKey: 'partnerships.service1Title',
      descriptionKey: 'partnerships.service1Desc'
    },
    {
      icon: Handshake,
      titleKey: 'partnerships.service2Title',
      descriptionKey: 'partnerships.service2Desc'
    },
    {
      icon: TrendingUp,
      titleKey: 'partnerships.service3Title',
      descriptionKey: 'partnerships.service3Desc'
    }
  ]

  const advantages = [
    {
      icon: Target,
      titleKey: 'partnerships.advantage1Title',
      descriptionKey: 'partnerships.advantage1Desc'
    },
    {
      icon: Users,
      titleKey: 'partnerships.advantage2Title',
      descriptionKey: 'partnerships.advantage2Desc'
    },
    {
      icon: Award,
      titleKey: 'partnerships.advantage3Title',
      descriptionKey: 'partnerships.advantage3Desc'
    }
  ]

  const process = [
    {
      step: '01',
      titleKey: 'partnerships.process1Title',
      descriptionKey: 'partnerships.process1Desc'
    },
    {
      step: '02',
      titleKey: 'partnerships.process2Title',
      descriptionKey: 'partnerships.process2Desc'
    },
    {
      step: '03',
      titleKey: 'partnerships.process3Title',
      descriptionKey: 'partnerships.process3Desc'
    },
    {
      step: '04',
      titleKey: 'partnerships.process4Title',
      descriptionKey: 'partnerships.process4Desc'
    }
  ]

  return (
    <div className="pt-26">
      {/* Hero Section avec image */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 to-red-700 text-white overflow-hidden">
        {/* Image de fond */}
        <div className="absolute inset-0">
          <img
            src="/images/partnerships-hero.webp"
            alt="Partenariats Internationaux"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-blue-900/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              {t('partnerships.pageTitle')}
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              {t('partnerships.pageSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <motion.div variants={fadeIn('up', 0.2)} className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                {t('partnerships.servicesTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('partnerships.servicesSubtitle')}
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn('up', index * 0.1)}
                  className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-orange-600 to-red-600 mb-6">
                    <service.icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-gray-600">{t(service.descriptionKey)}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section avec image au milieu */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image au milieu */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/partnerships-mid.webp"
                  alt="Collaboration Internationale"
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                
                {/* Badge informatif */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-lg"
                >
                  <div className="flex items-center space-x-2">
                    <Handshake className="w-5 h-5 text-orange-600" />
                    <span className="font-bold text-gray-900 text-sm">{t('partnerships.location')}</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Contenu Avantages */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
            >
              <motion.div variants={fadeIn('up', 0.2)} className="mb-8">
                <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                  {t('partnerships.advantagesTitle')}
                </h2>
                <p className="text-xl text-gray-600">
                  {t('partnerships.advantagesSubtitle')}
                </p>
              </motion.div>

              <motion.div
                variants={staggerContainer}
                className="space-y-6"
              >
                {advantages.map((advantage, index) => (
                  <motion.div
                    key={index}
                    variants={fadeIn('up', index * 0.1)}
                    className="flex items-start space-x-4 p-6 bg-white rounded-xl hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-orange-100 to-red-100 flex-shrink-0">
                      <advantage.icon size={24} className="text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
                        {t(advantage.titleKey)}
                      </h3>
                      <p className="text-gray-600">{t(advantage.descriptionKey)}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div variants={fadeIn('up', 0.2)} className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                {t('partnerships.processTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('partnerships.processSubtitle')}
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {process.map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn('up', index * 0.1)}
                  className="relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-xl shadow-lg"
                >
                  <div className="text-6xl font-bold text-orange-200 mb-4">{item.step}</div>
                  <h3 className="text-xl font-heading font-bold text-gray-900 mb-3">
                    {t(item.titleKey)}
                  </h3>
                  <p className="text-gray-600">{t(item.descriptionKey)}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-orange-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              {t('partnerships.closingTitle')}
            </h2>
            <p className="text-xl">
              {t('partnerships.closingText')}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Partnerships